'use client';

import React from 'react';
import styled from 'styled-components';

export interface SegmentData {
  status: 'up' | 'down' | 'paused';
  time?: string;
  label?: string;
}

export interface UptimeBarProps {
  /** Uptime percentage (0-100) */
  uptime: number;
  /** Number of segments to display */
  segments?: number;
  /** Custom uptime data for each segment. Accepts:
   *  - 'up' | 'down' | 'paused'
   *  - boolean (true=up, false=down)
   *  - number (1=up, 0=down, 2=paused)
   *  - SegmentData object with status, time, and label
   */
  data?: Array<'up' | 'down' | 'paused' | SegmentData> | boolean[] | number[];
  /** Show tooltips on hover */
  showTooltip?: boolean;
  className?: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const BarContainer = styled.div`
  display: flex;
  gap: 2px;
  width: 100%;
  height: 24px;
`;

const SegmentWrapper = styled.div`
  position: relative;
  flex: 1;
`;

const Segment = styled.div<{ $status: 'up' | 'down' | 'paused' }>`
  width: 100%;
  height: 100%;
  background-color: ${({ $status }) => (
    $status === 'up' ? '#10b981' : $status === 'down' ? '#ef4444' : '#9CA3AF'
  )};
  border-radius: 2px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
    transform: scaleY(1.1);
  }
`;

const Tooltip = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  padding: 8px 12px;
  background: #1f2937;
  color: #ffffff;
  font-size: 12px;
  border-radius: 6px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: #1f2937;
  }

  ${SegmentWrapper}:hover & {
    opacity: 1;
    visibility: visible;
  }
`;

const TooltipLabel = styled.div`
  font-weight: 600;
  margin-bottom: 2px;
`;

const TooltipTime = styled.div`
  font-size: 11px;
  opacity: 0.8;
`;

/**
 * UptimeBar Component
 * 
 * Displays uptime as a series of colored segments with optional tooltips
 */
export const UptimeBar: React.FC<UptimeBarProps> = ({
  uptime,
  segments = 24,
  data,
  showTooltip = true,
  className,
}) => {
  // Generate segment data based on uptime percentage if custom data not provided
  const generateSegments = (): SegmentData[] => {
    if (data) {
      return (data as Array<'up' | 'down' | 'paused' | SegmentData | boolean | number>).map((d, index) => {
        // If it's already a SegmentData object
        if (typeof d === 'object' && 'status' in d) {
          return d as SegmentData;
        }
        
        // Convert other types to SegmentData
        let status: 'up' | 'down' | 'paused';
        if (typeof d === 'string') {
          status = d as 'up' | 'down' | 'paused';
        } else if (typeof d === 'boolean') {
          status = d ? 'up' : 'down';
        } else {
          // number
          if (d === 1) status = 'up';
          else if (d === 2) status = 'paused';
          else status = 'down';
        }
        
        return {
          status,
          time: `Hour ${index + 1}`,
          label: status === 'up' ? 'Up' : status === 'down' ? 'Down' : 'Paused'
        };
      });
    }

    const uptimeDecimal = uptime / 100;
    const upSegments = Math.round(segments * uptimeDecimal);
    
    return Array.from({ length: segments }, (_, i) => ({
      status: i < upSegments ? 'up' : 'down',
      time: `Hour ${i + 1}`,
      label: i < upSegments ? 'Up' : 'Down'
    }));
  };

  const segmentStatuses = generateSegments();

  return (
    <Container className={className}>
      <BarContainer>
        {segmentStatuses.map((segment, index) => (
          <SegmentWrapper key={index}>
            <Segment $status={segment.status} />
            {showTooltip && (
              <Tooltip>
                <TooltipLabel>{segment.label}</TooltipLabel>
                {segment.time && <TooltipTime>{segment.time}</TooltipTime>}
              </Tooltip>
            )}
          </SegmentWrapper>
        ))}
      </BarContainer>
    </Container>
  );
};

export default UptimeBar;

